import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { FieldRenderContext } from '@cli-types/next-adapter/forms.js'
import { singularize, toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { formItemProps } from '@next-adapter/generators/form/form-shared/form-item-props.js'
import { formLabel } from '@next-adapter/generators/form/form-shared/form-label.js'
import { labelWithDescription } from '@next-adapter/generators/form/form-shared/label-with-description.js'
import { renderMultiSelectFieldRenderer } from '@next-adapter/templates/renderers/generators/form/field-jsx/render-multi-select-field.js'
import { defaultValueFromSourceHandler } from './default-value-from-source-handler.js'

export function renderMultiSelectField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const fieldPascal = toPascalCase(field.name)
  const isCreatable = field.creatable && context.creatableSelectFieldNames?.has(field.name)
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const optionsVar = isCreatable ? `${field.name}Options` : 'staticOptions'
  const staticOptions = `[${(field.options ?? []).map((option) => `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`).join(', ')}]`
  const openVar = `${field.name}Open`
  const setOpenVar = `set${fieldPascal}Open`
  const searchVar = `${field.name}Search`
  const setSearchVar = `set${fieldPascal}Search`
  const pendingVar = `create${fieldPascal}OptionPending`
  const createHandler = `handleCreate${fieldPascal}Option`
  const placeholder = field.placeholder || `Select ${label.toLowerCase()}`
  const previewLimit = Math.max(1, Math.floor(field.previewLimit ?? 1))
  const lowerLabel = label.toLowerCase()
  const optionsId = `${context.schemaName ? `${toKebabCase(singularize(context.schemaName))}-` : ''}${toKebabCase(field.name)}-options`
  const optionsDeclaration = isCreatable
    ? `const options = ${optionsVar}`
    : renderMultiSelectFieldRenderer({
        kind: 'renderMultiSelectFieldStaticOptionsTemplate',
        props: {
          staticOptions: staticOptions,
          indent: indent
        }
      })
  const searchInput = isCreatable
    ? renderMultiSelectFieldRenderer({
        kind: 'renderMultiSelectFieldContentTemplate',
        props: {
          indent: indent,
          lowerLabel: lowerLabel,
          searchVar: searchVar,
          setSearchVar: setSearchVar
        }
      })
    : `<CommandInput placeholder="Search ${lowerLabel}..." />`
  const createState = isCreatable
    ? renderMultiSelectFieldRenderer({
        kind: 'renderMultiSelectFieldCreateLabelTemplate',
        props: {
          indent: indent,
          searchVar: searchVar
        }
      })
    : ''
  const createAction = isCreatable
    ? renderMultiSelectFieldRenderer({
        kind: 'renderMultiSelectFieldActionButtonsTemplate',
        props: {
          indent: indent,
          pendingVar: pendingVar,
          createHandler: createHandler
        }
      })
    : ''
  const clearSearch = isCreatable ? `\n${indent}                        ${setSearchVar}('')` : ''

  return renderMultiSelectFieldRenderer({
    kind: 'renderMultiSelectFieldActionButtonsSerializedValueTemplate',
    props: {
      indent: indent,
      fieldName: field.name,
      optionsId: optionsId,
      optionsDeclaration: optionsDeclaration,
      createState: createState,
      previewLimit: previewLimit,
      formItemProps: formItemProps(field),
      labelWithDescription: labelWithDescription(
        formLabel(field, label),
        hintJSX,
        `${indent}        `
      ),
      openVar: openVar,
      setOpenVar: setOpenVar,
      stringify: JSON.stringify(placeholder),
      searchInput: searchInput,
      lowerLabel: lowerLabel,
      defaultValueFromHandlerContent: defaultValueFromHandler
        ? `${indent}                          ${defaultValueFromHandler}(nextValues)\n`
        : '',
      clearSearch: clearSearch,
      createAction: createAction
    }
  })
}
